// src/services/usuarios.js

import AuthService from "../auth/auth.js";

/**
 * Servicio para manejar todas las operaciones relacionadas con usuarios
 * Adaptado específicamente a la estructura de tu backend Django
 */
class UsuariosService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpoint = "/usuarios/api/usuarios/";

    // Sistema de caché para optimizar requests
    this.cache = {
      usuarios: new Map(), // Caché por ID individual
      todosPorTipo: new Map(), // Caché por tipo_usuario
      todosLosUsuarios: null, // Caché de todos los usuarios
      ultimaActualizacion: null,
      tiempoExpiracion: 5 * 60 * 1000, // 5 minutos
    };

    // MAPEO REAL BASADO EN TU BACKEND
    this.tiposUsuario = {
      1: { id: 1, nombre: "Cliente", codigo: "CLIENTE" },
      2: { id: 2, nombre: "Administrador", codigo: "ADMIN" },
      4: { id: 4, nombre: "Gestor/Supervisor", codigo: "GESTOR" },
      5: { id: 5, nombre: "Operador/Técnico", codigo: "OPERADOR" },
      6: { id: 6, nombre: "Invitado/Auditor", codigo: "INVITADO" },
      7: { id: 7, nombre: "Encargado", codigo: "ENCARGADO" },
    };
  }

  // ==========================================
  // MÉTODOS PRINCIPALES
  // ==========================================

  /**
   * MÉTODO PRINCIPAL: Obtener todos los usuarios del backend
   * @param {boolean} forzarActualizacion - Ignorar caché y obtener datos frescos
   * @returns {Promise<Array>} Array de usuarios mapeados
   */
  async obtenerTodosLosUsuarios(forzarActualizacion = false) {
    try {
      // Verificar caché si no se fuerza actualización
      if (!forzarActualizacion && this.esCacheValido() && this.cache.todosLosUsuarios) {
        return this.cache.todosLosUsuarios;
      }

      // OBTENER DATOS REALES DE TU BACKEND
      const response = await AuthService.authenticatedRequest(this.endpoint);

      // MANEJAR RESPUESTA PAGINADA (tu backend usa "results")
      let usuarios = [];
      if (response.results && Array.isArray(response.results)) {
        usuarios = response.results;
      } else {
        usuarios = [];
      }

      // MAPEAR USUARIOS CON TU ESTRUCTURA REAL
      const usuariosMapeados = usuarios.map((usuario) => this.mapearUsuarioReal(usuario));

      // Actualizar caché
      this.actualizarCacheCompleto(usuariosMapeados);

      return usuariosMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * OBTENER USUARIOS POR TIPO (encargados, admins, etc.)
   * @param {number|string} tipoUsuario - ID o código del tipo
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Array de usuarios del tipo especificado
   */
  async obtenerUsuariosPorTipo(tipoUsuario, forzarActualizacion = false) {
    try {
      const tipoId = this.normalizarTipoUsuario(tipoUsuario);

      // Verificar caché específico por tipo
      if (!forzarActualizacion && this.esCacheValido() && this.cache.todosPorTipo.has(tipoId)) {
        return this.cache.todosPorTipo.get(tipoId);
      }

      // Obtener todos los usuarios y filtrar
      const todosLosUsuarios = await this.obtenerTodosLosUsuarios(forzarActualizacion);

      const usuariosFiltrados = todosLosUsuarios.filter((usuario) => {
        return usuario.tipo_usuario === tipoId;
      });

      // Cachear por tipo
      this.cache.todosPorTipo.set(tipoId, usuariosFiltrados);

      return usuariosFiltrados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * MÉTODO ESPECÍFICO: Obtener solo encargados (tipo_usuario = 7)
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Array de usuarios encargados
   */
  async obtenerEncargados(forzarActualizacion = false) {
    return await this.obtenerUsuariosPorTipo(7, forzarActualizacion);
  }

  /**
   * MÉTODO ESPECÍFICO: Obtener administradores (tipo_usuario = 2)
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Array de administradores
   */
  async obtenerAdministradores(forzarActualizacion = false) {
    return await this.obtenerUsuariosPorTipo(2, forzarActualizacion);
  }

  /**
   * OBTENER USUARIO ESPECÍFICO POR ID
   * @param {number} userId - ID del usuario
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Object>} Datos completos del usuario
   */
  async obtenerUsuarioPorId(userId, forzarActualizacion = false) {
    try {
      // Verificar caché individual
      if (!forzarActualizacion && this.cache.usuarios.has(userId)) {
        const usuarioCacheado = this.cache.usuarios.get(userId);
        const tiempoTranscurrido = Date.now() - usuarioCacheado.timestamp;

        if (tiempoTranscurrido < this.cache.tiempoExpiracion) {
          return usuarioCacheado.data;
        }
      }

      // OBTENER DEL BACKEND REAL
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${userId}/`);

      // Mapear y cachear
      const usuarioMapeado = this.mapearUsuarioReal(response);
      this.cache.usuarios.set(userId, {
        data: usuarioMapeado,
        timestamp: Date.now(),
      });

      return usuarioMapeado;
    } catch (error) {
      // Retornar fallback en caso de error
      return this.crearUsuarioFallback(userId);
    }
  }

  /**
   * OBTENER MÚLTIPLES USUARIOS POR IDS (optimizado)
   * @param {Array<number>} userIds - Array de IDs de usuarios
   * @param {boolean} forzarActualizacion - Ignorar caché
   * @returns {Promise<Array>} Array de usuarios mapeados
   */
  async obtenerUsuariosPorIds(userIds, forzarActualizacion = false) {
    try {
      const usuarios = [];
      const idsParaCargar = [];

      // Verificar caché para cada ID
      if (!forzarActualizacion) {
        for (const userId of userIds) {
          if (this.cache.usuarios.has(userId)) {
            const usuarioCacheado = this.cache.usuarios.get(userId);
            const tiempoTranscurrido = Date.now() - usuarioCacheado.timestamp;

            if (tiempoTranscurrido < this.cache.tiempoExpiracion) {
              usuarios.push(usuarioCacheado.data);
              continue;
            }
          }
          idsParaCargar.push(userId);
        }
      } else {
        idsParaCargar.push(...userIds);
      }

      // Cargar usuarios que no están en caché
      if (idsParaCargar.length > 0) {
        const promesasUsuarios = idsParaCargar.map((userId) => this.obtenerUsuarioPorId(userId, true));

        const usuariosNuevos = await Promise.allSettled(promesasUsuarios);

        usuariosNuevos.forEach((resultado, index) => {
          if (resultado.status === "fulfilled") {
            usuarios.push(resultado.value);
          } else {
            usuarios.push(this.crearUsuarioFallback(idsParaCargar[index]));
          }
        });
      }

      return usuarios;
    } catch (error) {
      // Retornar fallbacks para todos los IDs
      return userIds.map((id) => this.crearUsuarioFallback(id));
    }
  }

  /**
   * BUSCAR USUARIOS POR TEXTO (nombre, apellido, email, rut)
   * @param {string} texto - Texto a buscar
   * @param {number|string|null} tipoUsuario - Filtrar por tipo (opcional)
   * @param {boolean} buscarEnBackend - Buscar en backend o solo en caché
   * @returns {Promise<Array>} Usuarios que coinciden con la búsqueda
   */
  async buscarUsuarios(texto, tipoUsuario = null, buscarEnBackend = false) {
    try {
      let usuarios;
      if (buscarEnBackend) {
        // Buscar directamente en el backend (futuro: implementar búsqueda en backend)
        usuarios = await this.obtenerTodosLosUsuarios(true);
      } else {
        // Buscar en caché o cargar todos si no hay caché
        usuarios = await this.obtenerTodosLosUsuarios();
      }

      // Filtrar por tipo si se especifica
      if (tipoUsuario) {
        const tipoId = this.normalizarTipoUsuario(tipoUsuario);
        usuarios = usuarios.filter((u) => u.tipo_usuario === tipoId);
      }

      // Filtrar por texto
      const textoLower = texto.toLowerCase();
      const usuariosFiltrados = usuarios.filter((usuario) => {
        const nombreCompleto = usuario.nombre_completo.toLowerCase();
        const email = usuario.email.toLowerCase();
        const rut = usuario.rut || "";

        return nombreCompleto.includes(textoLower) || email.includes(textoLower) || rut.includes(texto);
      });

      return usuariosFiltrados;
    } catch (error) {
      return [];
    }
  }

  // ==========================================
  // MAPEO ESPECÍFICO PARA TU BACKEND
  // ==========================================

  /**
   * MAPEAR USUARIO REAL DE TU BACKEND
   * @param {Object} usuarioBackend - Usuario tal como viene de tu API
   * @returns {Object} Usuario mapeado para frontend
   */
  mapearUsuarioReal(usuarioBackend) {
    try {
      const tipoInfo = this.tiposUsuario[usuarioBackend.tipo_usuario] || this.tiposUsuario[1];

      return {
        // CAMPOS DIRECTOS DE TU BACKEND
        id: usuarioBackend.id,
        email: usuarioBackend.email,
        nombre: usuarioBackend.nombre || "",
        apellido: usuarioBackend.apellido || "",
        rut: usuarioBackend.rut || null,
        telefono: this._formatTelefono(usuarioBackend.telefono),

        // CAMPOS ESPECÍFICOS DE TU BACKEND
        is_superuser: usuarioBackend.is_superuser || false,
        is_staff: usuarioBackend.is_staff || false,
        is_active: usuarioBackend.is_active !== false,
        date_joined: usuarioBackend.date_joined,
        last_login: usuarioBackend.last_login,

        // RELACIONES CON OTROS MODELOS
        empresa_id: usuarioBackend.empresa,
        tipo_usuario: usuarioBackend.tipo_usuario,

        // INFORMACIÓN DE TIPO/ROL MAPEADA
        tipo_nombre: tipoInfo.nombre,
        tipo_codigo: tipoInfo.codigo,

        // PERMISOS (de tu backend)
        groups: usuarioBackend.groups || [],
        user_permissions: usuarioBackend.user_permissions || [],

        // CAMPOS COMPUTADOS PARA FRONTEND
        nombre_completo: this.construirNombreCompleto(usuarioBackend.nombre, usuarioBackend.apellido),
        iniciales: this.obtenerIniciales(usuarioBackend.nombre, usuarioBackend.apellido),

        // CAMPOS DE ESTADO COMPUTADOS
        es_administrador: usuarioBackend.is_superuser || usuarioBackend.is_staff || usuarioBackend.tipo_usuario === 2,
        es_encargado: usuarioBackend.tipo_usuario === 7,
        es_activo: usuarioBackend.is_active,

        // FECHAS FORMATEADAS
        fecha_creacion_formateada: this.formatearFecha(usuarioBackend.date_joined),
        ultimo_login_formateado: this.formatearFecha(usuarioBackend.last_login),

        // METADATA
        _mapeadoEn: new Date().toISOString(),
        _fuente: "backend",
      };
    } catch (error) {
      return this.crearUsuarioFallback(usuarioBackend.id || "error");
    }
  }

  /**
   * Formatea un número de teléfono (asume formato Chile)
   * @param {string | number} digits - El número, ej: 56985746190
   * @returns {string} - El número formateado, ej: +56 9 8574 6190
   */
  _formatTelefono(digits) {
    if (!digits) return "No disponible";

    let str = String(digits).replace(/\D/g, "");

    // Asumimos que viene 569...
    if (str.length === 11 && str.startsWith("569")) {
      return `+${str.substring(0, 2)} ${str.substring(2, 3)} ${str.substring(3, 7)} ${str.substring(7, 11)}`;
    }

    // Si no, solo retornamos el número con un + (si no lo tiene)
    if (str.startsWith("+")) return str;
    return `+${str}`;
  }

  /**
   * Construir nombre completo manejando casos edge
   * @param {string} nombre - Nombre del usuario
   * @param {string} apellido - Apellido del usuario
   * @returns {string} Nombre completo
   */
  construirNombreCompleto(nombre, apellido) {
    const nombreLimpio = (nombre || "").trim();
    const apellidoLimpio = (apellido || "").trim();

    if (!nombreLimpio && !apellidoLimpio) {
      return "Usuario sin nombre";
    }

    if (!nombreLimpio) return apellidoLimpio;
    if (!apellidoLimpio) return nombreLimpio;

    return `${nombreLimpio} ${apellidoLimpio}`;
  }

  /**
   * Formatear fecha para display
   * @param {string} fechaISO - Fecha en formato ISO
   * @returns {string} Fecha formateada
   */
  formatearFecha(fechaISO) {
    if (!fechaISO) return null;

    try {
      const fecha = new Date(fechaISO);
      return fecha.toLocaleDateString("es-CL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return fechaISO;
    }
  }

  /**
   * Crear usuario fallback cuando hay errores
   * @param {number|string} userId - ID del usuario
   * @returns {Object} Usuario con datos mínimos
   */
  crearUsuarioFallback(userId) {
    return {
      id: userId,
      email: "no-disponible@local.com",
      nombre: "Usuario",
      apellido: "No Disponible",
      rut: null,
      is_superuser: false,
      is_staff: false,
      is_active: false,
      date_joined: null,
      last_login: null,
      empresa_id: null,
      tipo_usuario: 1,
      tipo_nombre: "Cliente",
      tipo_codigo: "CLIENTE",
      groups: [],
      user_permissions: [],
      nombre_completo: `Usuario ID: ${userId}`,
      iniciales: "U?",
      es_administrador: false,
      es_encargado: false,
      es_activo: false,
      fecha_creacion_formateada: null,
      ultimo_login_formateado: null,
      _mapeadoEn: new Date().toISOString(),
      _fuente: "fallback",
    };
  }

  /**
   * Obtener iniciales de nombre y apellido
   * @param {string} nombre - Nombre del usuario
   * @param {string} apellido - Apellido del usuario
   * @returns {string} Iniciales
   */
  obtenerIniciales(nombre, apellido) {
    const inicial1 = nombre && nombre.trim() ? nombre.trim().charAt(0).toUpperCase() : "";
    const inicial2 = apellido && apellido.trim() ? apellido.trim().charAt(0).toUpperCase() : "";
    return inicial1 + inicial2 || "U";
  }

  /**
   * Normalizar tipo de usuario a ID numérico
   * @param {number|string} tipo - Tipo como ID o código
   * @returns {number} ID numérico del tipo
   */
  normalizarTipoUsuario(tipo) {
    if (typeof tipo === "number") return tipo;

    // Buscar por código
    const tipoEncontrado = Object.values(this.tiposUsuario).find((t) => t.codigo.toLowerCase() === tipo.toLowerCase());

    return tipoEncontrado ? tipoEncontrado.id : 1; // Default: Cliente
  }

  // ==========================================
  // GESTIÓN DE CACHÉ OPTIMIZADA
  // ==========================================

  /**
   * Actualizar caché completo con nuevos usuarios
   * @param {Array} usuarios - Usuarios mapeados
   */
  actualizarCacheCompleto(usuarios) {
    // Limpiar cachés anteriores
    this.cache.usuarios.clear();
    this.cache.todosPorTipo.clear();

    // Cachear usuarios individuales y agrupar por tipo
    const usuariosPorTipo = {};

    usuarios.forEach((usuario) => {
      // Caché individual
      this.cache.usuarios.set(usuario.id, {
        data: usuario,
        timestamp: Date.now(),
      });

      // Agrupar por tipo
      const tipoId = usuario.tipo_usuario;
      if (!usuariosPorTipo[tipoId]) {
        usuariosPorTipo[tipoId] = [];
      }
      usuariosPorTipo[tipoId].push(usuario);
    });

    // Cachear por tipo
    Object.entries(usuariosPorTipo).forEach(([tipoId, usuarios]) => {
      this.cache.todosPorTipo.set(parseInt(tipoId), usuarios);
    });

    // Cachear todos los usuarios
    this.cache.todosLosUsuarios = usuarios;
    this.cache.ultimaActualizacion = Date.now();
  }

  /**
   * Verificar si el caché global es válido
   * @returns {boolean} True si el caché es válido
   */
  esCacheValido() {
    if (!this.cache.ultimaActualizacion) return false;

    const tiempoTranscurrido = Date.now() - this.cache.ultimaActualizacion;
    return tiempoTranscurrido < this.cache.tiempoExpiracion;
  }

  /**
   * Limpiar caché manualmente
   */
  limpiarCache() {
    this.cache.usuarios.clear();
    this.cache.todosPorTipo.clear();
    this.cache.todosLosUsuarios = null;
    this.cache.ultimaActualizacion = null;
  }

  /**
   * Obtener estadísticas del caché
   * @returns {Object} Estadísticas del caché
   */
  obtenerEstadisticasCache() {
    return {
      usuariosEnCache: this.cache.usuarios.size,
      tiposEnCache: this.cache.todosPorTipo.size,
      todosUsuariosCacheados: !!this.cache.todosLosUsuarios,
      totalUsuarios: this.cache.todosLosUsuarios?.length || 0,
      ultimaActualizacion: this.cache.ultimaActualizacion,
      esValido: this.esCacheValido(),
      tiempoExpiracion: this.cache.tiempoExpiracion,
      tiempoRestante: this.cache.ultimaActualizacion
        ? Math.max(0, this.cache.tiempoExpiracion - (Date.now() - this.cache.ultimaActualizacion))
        : 0,
    };
  }

  // ==========================================
  // MANEJO DE ERRORES
  // ==========================================

  /**
   * Manejar errores de las llamadas a la API
   * @param {Error} error - Error original
   * @returns {Error} Error procesado
   */
  manejarError(error) {
    let mensaje = "Error desconocido al obtener usuarios";
    let codigo = "USUARIOS_ERROR";

    if (error.code === "ECONNABORTED") {
      mensaje = "Tiempo de espera agotado al obtener usuarios";
      codigo = "USUARIOS_TIMEOUT";
    } else if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          mensaje = "No autorizado para obtener usuarios";
          codigo = "USUARIOS_UNAUTHORIZED";
          break;
        case 403:
          mensaje = "Sin permisos para acceder a usuarios";
          codigo = "USUARIOS_FORBIDDEN";
          break;
        case 404:
          mensaje = "Usuarios no encontrados";
          codigo = "USUARIOS_NOT_FOUND";
          break;
        default:
          mensaje = `Error del servidor al obtener usuarios (${status})`;
          codigo = "USUARIOS_SERVER_ERROR";
      }
    } else if (error.request) {
      mensaje = "Error de conexión al obtener usuarios";
      codigo = "USUARIOS_NETWORK_ERROR";
    }

    const errorProcesado = new Error(mensaje);
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }
}

// Exportar instancia única del servicio
export default new UsuariosService();
